import { localeForCurrency } from '../efficacy/formatters';
import { MONEY_SCALES } from './constants';

/**
 * Formato numérico del informe de existencias. Todo cuelga de la moneda
 * activa: con MXN el locale es es-MX (`52,653`, `$1,784,455`), con EUR es
 * es-ES (`52.653`, `1.784.455 €`). Las unidades siguen el mismo locale que
 * el dinero para que una fila no mezcle separadores.
 *
 * Los importes llegan del back en MXN; aquí se aplica el tipo de cambio.
 */

// Intl devuelve el guion del teclado; en columnas de cifras el menos
// tipográfico alinea con el más.
const typographicMinus = (s) => s.replace(/-/g, '−');

// es-ES no agrupa millares de cuatro cifras (regla min2 del CLDR); en una
// columna de cifras eso desalinea `9876` con `12.345`, así que se fuerza.
const intl = (locale, opts) => new Intl.NumberFormat(locale, { useGrouping: 'always', ...opts });

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** `2026-08-31` → `31 ago 2026` (o `31 ago` con `withYear=false`). Sin Intl: «sept» no. */
export const shortDay = (ymd, withYear = true) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}${withYear ? ` ${y}` : ''}`;
};

/** Etiquetas de las dos fechas en cabeceras: sin año si coinciden, con año corto si no. */
export const dateColumnLabels = (date, compareDate, todayYmd) => {
  const a = date || todayYmd;
  const b = compareDate || '';
  const sameYear = a.slice(0, 4) === b.slice(0, 4);
  const lbl = (ymd) => (sameYear ? shortDay(ymd, false) : `${shortDay(ymd, false)} ${ymd.slice(2, 4)}`);
  return { current: date ? lbl(a) : 'Actual', compare: b ? lbl(b) : '' };
};

export const makeFormatters = (currency, moneyScaleKey) => {
  const locale = localeForCurrency(currency.symbol);
  const scale = MONEY_SCALES.find((s) => s.key === moneyScaleKey) || MONEY_SCALES[0];
  const ex = Number(currency.exchange) || 1;

  const units = intl(locale, { maximumFractionDigits: 0 });
  const pct = intl(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: 'exceptZero' });
  // Por encima de ±1000 % el decimal no informa y no cabe en la celda.
  const pctBig = intl(locale, { maximumFractionDigits: 0, signDisplay: 'exceptZero' });
  const fmtPct = (p) => (Math.abs(p) >= 1000 ? pctBig : pct).format(p);
  // Con escala (miles o millones), dos decimales: `111,45 k€`, `3,82 M€`.
  const scaledDigits = scale.key === 'full' ? 0 : 2;
  const scaled = intl(locale, { minimumFractionDigits: scaledDigits, maximumFractionDigits: scaledDigits });
  const signedScaled = intl(locale, {
    minimumFractionDigits: scaledDigits,
    maximumFractionDigits: scaledDigits,
    signDisplay: 'exceptZero',
  });
  const moneyFull = intl(locale, {
    style: 'currency',
    currency: currency.symbol,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  });
  const shareFmt = intl(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  // Símbolo de la moneda tal como lo pinta el locale (`$`, `€`).
  const parts = moneyFull.formatToParts(1);
  const symbol = parts.find((p) => p.type === 'currency')?.value || currency.symbol;

  /**
   * Importe en la escala elegida, con la moneda. Sin escala, el formato
   * nativo del locale (`$1,376,000`, `1.376.000 €`). En miles o millones la
   * letra va pegada al símbolo como unidad tras el número (`1,376 k$`,
   * `3,82 M€`). Mismo criterio que `fmtMoneyScaled` de los PDFs del back.
   */
  const money = (v, signed = false) => {
    const n = Number(v) * ex;
    if (scale.key === 'full') {
      const out = typographicMinus(moneyFull.format(n));
      return signed && n > 0 ? `+${out}` : out;
    }
    const num = (signed ? signedScaled : scaled).format(n / scale.factor);
    return `${typographicMinus(num)} ${scale.key}${symbol}`;
  };

  /** Celda de tabla: unidades enteras; importes con moneda en la escala elegida. */
  const cell = (kind, v) => {
    if (v === null || v === undefined || !Number.isFinite(Number(v))) return '';
    if (kind === 'money') return money(v);
    return typographicMinus(units.format(Math.round(Number(v))));
  };

  /** Variación absoluta, con signo. */
  const signedCell = (kind, v) => {
    if (v === null || v === undefined || !Number.isFinite(Number(v))) return '';
    if (kind === 'money') return money(v, true);
    return typographicMinus(
      intl(locale, { maximumFractionDigits: 0, signDisplay: 'exceptZero' }).format(Math.round(v)),
    );
  };

  /**
   * Δ % sin símbolo (la unidad va en la cabecera). El back manda
   * `deltaPct: null` cuando no hay base: comparación 0 con valor actual
   * distinto de 0 → «nueva»; comparación negativa (stock negativo) →
   * «sin base»; el resto, vacío.
   */
  const deltaPct = (c) => {
    if (!c) return '';
    if (c.deltaPct === null || c.deltaPct === undefined) {
      const cmp = Number(c.compare) || 0;
      if (cmp === 0 && c.value) return 'nueva';
      if (cmp < 0) return 'sin base';
      return '';
    }
    return typographicMinus(fmtPct(c.deltaPct * 100));
  };

  /** KPI y tooltips: cifra completa con símbolo (`$91,874,448`, `4.593.472 €`). */
  const kpi = (kind, v) => {
    if (v === null || v === undefined) return '';
    if (kind !== 'money') return typographicMinus(units.format(Math.round(v)));
    return typographicMinus(moneyFull.format(Number(v) * ex));
  };

  /**
   * Representación: parte que es `v` de `base`, con un decimal y sin signo
   * forzado (`12,3 %`). Vacío si no hay base.
   */
  const share = (v, base) => {
    const b = Number(base);
    if (!b || !Number.isFinite(b) || v === null || v === undefined) return '';
    return `${typographicMinus(shareFmt.format((Number(v) / b) * 100))} %`;
  };

  const pctText = (p) => (p === null || p === undefined ? '' : `${typographicMinus(fmtPct(p * 100))} %`);

  return { locale, cell, signedCell, deltaPct, kpi, share, pctText, symbol, scale };
};
